import { motion, useReducedMotion } from 'framer-motion'
import { DollarSign, Send, TrendingDown, TrendingUp } from 'lucide-react'
import { PipelineForecastHeroCard } from '@/components/Dashboard/PipelineForecastHeroCard'
import {
  OPERACAO_METRIC_ACCENTS,
  OperacaoMetricCard,
} from '@/components/Dashboard/OperacaoMetricCard'
import { getDashboardVariants } from '@/components/Dashboard/dashboardMotion'
import {
  getPipelineWinRateDisplay,
  type PipelineTabView,
} from '@/components/Dashboard/pipelineTab.utils'
import {
  formatDashboardCurrency,
  formatDashboardNumber,
} from '@/utils/crmValuesDisplay'
import { seriesHasVariation, trendChangePercent } from '@/utils/kpiTrend'

type Props = {
  view: PipelineTabView
  valuesVisible?: boolean
}

export function PipelineKpiGrid({ view, valuesVisible = false }: Props) {
  const reduced = useReducedMotion()
  const v = getDashboardVariants(reduced)
  const winRate = getPipelineWinRateDisplay(view, valuesVisible)
  const revenueSeries = view.monthlyData.map((row) => row.revenue)
  const revenueTrendDelta = trendChangePercent(revenueSeries)
  const forecast = view.closingForecast

  const secondaryCards = [
    {
      key: 'closed-revenue',
      label: 'Receita fechada',
      value: formatDashboardCurrency(view.totalRevenue, valuesVisible),
      numericValue: view.totalRevenue,
      valueFormat: 'currency' as const,
      subtitle: `${formatDashboardNumber(view.totalWon, valuesVisible)} deals ganhos`,
      trendPositive: view.totalWon > 0,
      icon: <DollarSign size={16} />,
      accent: OPERACAO_METRIC_ACCENTS.closed,
    },
    {
      key: 'proposals-sent',
      label: 'Propostas enviadas',
      value: formatDashboardNumber(forecast.proposalsSentCount, valuesVisible),
      numericValue: forecast.proposalsSentCount,
      valueFormat: 'integer' as const,
      subtitle: `${formatDashboardCurrency(forecast.proposalsSentForecast, valuesVisible)} na previsão`,
      trendPositive: forecast.proposalsSentCount > 0,
      icon: <Send size={16} />,
      accent: { bg: '#ccfbf1', text: '#0d9488', ring: '#5eead4' },
    },
    {
      key: 'win-rate',
      label: 'Win rate',
      value: winRate.value,
      numericValue: view.winRate,
      valueFormat: 'percent' as const,
      subtitle: winRate.hint ?? 'Ganhos ÷ fechamentos totais',
      trendPositive: view.winRate >= 50,
      icon: <TrendingUp size={16} />,
      accent: OPERACAO_METRIC_ACCENTS.winRate,
    },
    {
      key: 'lost',
      label: 'Perdas no período',
      value: formatDashboardNumber(view.lostDeals, valuesVisible),
      numericValue: view.lostDeals,
      valueFormat: 'integer' as const,
      subtitle:
        view.closedCount > 0
          ? `${formatDashboardNumber(view.closedCount, valuesVisible)} fechamentos totais`
          : 'Sem fechamentos no período',
      trendPositive: view.lostDeals === 0,
      icon: <TrendingDown size={16} />,
      accent: { bg: '#fee2e2', text: '#dc2626', ring: '#fca5a5' },
    },
  ]

  return (
    <motion.div
      variants={v.kpiGrid}
      initial='hidden'
      animate='show'
      className='grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4 xl:grid-rows-2'
    >
      <motion.div variants={v.kpiItem} className='sm:col-span-2 xl:row-span-2'>
        <PipelineForecastHeroCard forecast={forecast} valuesVisible={valuesVisible} />
      </motion.div>

      {secondaryCards.map((kpi, index) => (
        <motion.div key={kpi.key} variants={v.kpiItem} className='min-h-[118px]'>
          <OperacaoMetricCard
            label={kpi.label}
            value={kpi.value}
            numericValue={kpi.numericValue}
            valueFormat={kpi.valueFormat}
            subtitle={kpi.subtitle}
            trendDelta={
              kpi.key === 'closed-revenue' && seriesHasVariation(revenueSeries)
                ? revenueTrendDelta
                : null
            }
            trendPositive={kpi.trendPositive}
            icon={kpi.icon}
            accent={kpi.accent}
            valuesVisible={valuesVisible}
            animationDelay={(index + 1) * 90}
            variant='compact'
          />
        </motion.div>
      ))}
    </motion.div>
  )
}
